import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  MapPin,
  Mail,
  Phone,
  CalendarDays,
  Stethoscope,
  Landmark,
  BadgeCheck,
  Copy,
} from "lucide-react";
import { BANK_DETAILS, SECRETARIAT, COMPLIANCE } from "@/lib/mockStore";
import { settingsApi } from "@/lib/api";
import { useSiteImage } from "@/hooks/useSiteImages";

const LOGO_API_DEFAULT =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_apicon-register/artifacts/1q137q9b_f04cf28b-14e3-4e14-b6c6-e1574fbf1d60.JPG";
const LOGO_ASSAM_DEFAULT =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_apicon-register/artifacts/ka43vom0_9012ac3f-853f-407b-aa81-8f5e96f3f583.JPG";

const copy = (val, label) => {
  try {
    navigator.clipboard.writeText(val);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed");
  }
};

const Footer = () => {
  const [partnersLabel, setPartnersLabel] = useState("Under the auspices of");
  const logoApi = useSiteImage("logoApi", LOGO_API_DEFAULT);
  const logoAssam = useSiteImage("logoAssam", LOGO_ASSAM_DEFAULT);
  useEffect(() => {
    settingsApi
      .get()
      .then((s) => {
        if (s && s.partnersLabel) setPartnersLabel(s.partnersLabel);
      })
      .catch(() => {});
  }, []);
  return (
    <footer
      className="relative overflow-hidden bg-apicon-navy text-white"
      data-testid="site-footer"
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] heritage-pattern" />

      <div className="relative mx-auto grid w-full max-w-7xl gap-12 px-6 py-16 md:px-8 md:py-20 lg:grid-cols-12">
        {/* Brand block */}
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20">
              <Stethoscope className="h-5 w-5 text-apicon-gold" />
            </span>
            <div>
              <div className="font-display text-2xl font-bold tracking-tight">
                APICON <span className="text-apicon-gold">2026</span>
              </div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/70">
                35th Annual Conference · API
              </div>
            </div>
          </div>

          <p className="mt-6 max-w-md font-display text-2xl italic leading-snug text-white/90 md:text-[26px]">
            &ldquo;Advancing Internal Medicine: Bridging Science &amp; Compassion.&rdquo;
          </p>

          <p className="mt-6 max-w-md text-sm leading-relaxed text-white/70">
            A gathering of physicians, researchers and educators in Tinsukia,
            Assam — where centuries-old tea gardens meet cutting-edge internal
            medicine.
          </p>
        </div>

        {/* Explore */}
        <div className="lg:col-span-2">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-apicon-gold">
            Explore
          </div>
          <ul className="mt-5 space-y-3 text-sm text-white/85">
            {[
              { to: "/", label: "Home" },
              { to: "/committee", label: "Committee" },
              { to: "/blog", label: "Blog" },
              { to: "/gallery", label: "Gallery" },
              { to: "/register", label: "Register" },
              { to: "/login", label: "Sign in" },
            ].map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="inline-flex items-center gap-2 text-white/80 transition-colors duration-200 hover:text-apicon-gold"
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-white/40" />
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Reach the secretariat */}
        <div className="lg:col-span-3">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-apicon-gold">
            Reach the secretariat
          </div>
          <ul className="mt-5 space-y-4 text-sm text-white/85">
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-apicon-gold" />
              <span>
                {SECRETARIAT.venue}
                <br />
                {SECRETARIAT.city}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-apicon-gold" />
              <span>{SECRETARIAT.dates}</span>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-apicon-gold" />
              <a
                href={`mailto:${SECRETARIAT.email}`}
                className="break-all hover:text-apicon-gold"
                data-testid="footer-email"
              >
                {SECRETARIAT.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-apicon-gold" />
              <div className="flex flex-col gap-1">
                {SECRETARIAT.phones.map((p, i) => (
                  <a
                    key={p}
                    href={`tel:${p.replace(/\s+/g, "")}`}
                    className="hover:text-apicon-gold"
                    data-testid={`footer-phone-${i}`}
                  >
                    {p}
                  </a>
                ))}
              </div>
            </li>
          </ul>
        </div>

        {/* Bank details */}
        <div className="lg:col-span-3">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-apicon-gold">
            Bank details
          </div>
          <div
            className="mt-5 space-y-3 text-sm text-white/85"
            data-testid="footer-bank-details"
          >
            <div className="flex items-start gap-3">
              <Landmark className="mt-0.5 h-4 w-4 shrink-0 text-apicon-gold" />
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">
                  Account name
                </div>
                <div className="mt-0.5 leading-snug">
                  {BANK_DETAILS.accountName}
                </div>
              </div>
            </div>
            {[
              { k: "Account #", v: BANK_DETAILS.accountNumber, id: "acc", mono: true },
              { k: "IFSC", v: BANK_DETAILS.ifsc, id: "ifsc", mono: true },
              { k: "Bank", v: BANK_DETAILS.bankName, id: "bank" },
              { k: "Branch", v: BANK_DETAILS.branch, id: "branch" },
            ].map((row) => (
              <div key={row.id} className="flex items-start gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/40" />
                <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">
                      {row.k}
                    </div>
                    <div
                      className={`mt-0.5 truncate ${row.mono ? "font-mono" : ""}`}
                    >
                      {row.v}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => copy(row.v, row.k)}
                    className="shrink-0 rounded-full border border-white/20 p-1.5 text-white/70 hover:border-apicon-gold hover:text-apicon-gold"
                    aria-label={`Copy ${row.k}`}
                    data-testid={`footer-copy-${row.id}`}
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10">
        {/* Compliance strip */}
        <div
          className="mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-4 border-b border-white/10 px-6 py-6 text-xs text-white/70 md:flex-row md:items-center md:px-8"
          data-testid="footer-compliance"
        >
          <div className="flex items-center gap-2">
            <BadgeCheck className="h-4 w-4 text-apicon-gold" />
            <span className="uppercase tracking-[0.22em] text-white/60">
              Compliance
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono">
            <span>
              <span className="text-white/50">GST</span>{" "}
              <span
                className="text-white/90"
                data-testid="footer-gst"
              >
                {COMPLIANCE.gst}
              </span>
            </span>
            <span>
              <span className="text-white/50">PAN</span>{" "}
              <span
                className="text-white/90"
                data-testid="footer-pan"
              >
                {COMPLIANCE.pan}
              </span>
            </span>
          </div>
        </div>

        {/* Partner logos row */}
        <div
          className="mx-auto flex w-full max-w-7xl flex-col items-center gap-4 border-b border-white/10 px-6 py-8 md:px-8"
          data-testid="footer-partner-logos"
        >
          <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60">
            {partnersLabel}
          </div>
          <div className="flex items-center gap-5 sm:gap-8">
            <img
              src={logoApi}
              alt="Association of Physicians of India"
              className="h-20 w-20 rounded-2xl bg-white object-contain p-1.5 ring-2 ring-white/25 sm:h-24 sm:w-24"
              data-testid="footer-logo-1"
            />
            <span className="h-10 w-px bg-white/15" aria-hidden />
            <img
              src={logoAssam}
              alt="API Assam Chapter"
              className="h-20 w-20 rounded-2xl bg-white object-contain p-1.5 ring-2 ring-white/25 sm:h-24 sm:w-24"
              data-testid="footer-logo-2"
            />
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-3 px-6 py-6 text-xs text-white/60 md:flex-row md:items-center md:px-8">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} APICON 2026 · Association of Physicians
            of India · Assam Chapter, Tinsukia Branch
          </div>
          <div className="flex items-center gap-5">
            <span>Privacy</span>
            <span>Terms</span>
            <span>Code of Conduct</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
